<template>
    <div class="carbon-management">
        <!-- 메인 컨테이너 -->
        <div class="content-container">
            <!-- 페이지 헤더 -->
            <CarbonHeader
                :org-list="orgList"
                :selected-org-idx="selectedOrgIdx"
                :selected-year="selectedYear"
                :selected-month="selectedMonth"
                :view-mode="viewMode"
                :display-unit="displayUnit"
                :dashboard-loading="dashboardLoading"
                @update:selectedOrgIdx="selectedOrgIdx = $event"
                @update:selectedYear="selectedYear = $event"
                @update:selectedMonth="selectedMonth = $event"
                @update:viewMode="viewMode = $event"
                @update:displayUnit="displayUnit = $event"
                @refresh="handleDashboardRefresh"
                @settings-click="handleSettingsClick"
                @report-click="openReportModal"
            />

            <!-- 메인 콘텐츠 -->
            <div class="main-content">
                <!-- 배출량 현황 -->
                <div class="tab-content">
                    <CarbonOverview
                        ref="dashboard"
                        :selected-org-idx="selectedOrgIdx"
                        :selected-year="selectedYear"
                        :selected-month="selectedMonth"
                        :view-mode="viewMode"
                        :display-unit="displayUnit"
                        :loginUser="loginUser"
                        :mockData="mockDashboardData"
                        @data-updated="handleDataUpdate"
                        @loading="dashboardLoading = $event"
                        @update:selectedMonth="selectedMonth = $event"
                    />
                </div>
            </div>
        </div>

        <!-- 설정 모달 -->
        <SettingsModal
            :visible="showSettingsModal"
            :loginUser="loginUser"
            :initial-menu="settingsInitialMenu"
            @close="handleSettingsClose"
            @boundary-saved="handleBoundarySaved"
            @data-imported="handleDataImported"
            @target-saved="handleTargetSaved"
        />

        <!-- 보고서 모달 -->
        <ReportModal ref="reportModal" :loginUser="loginUser" @report-generated="handleReportGenerated" />

        <!-- 도움말 모달 -->
        <b-modal v-model="showHelpModal" title="탄소 배출 관리 도움말" size="lg" centered>
            <div class="help-content">
                <h6 class="text-primary mb-3">
                    <i class="mdi mdi-information mr-2"></i>
                    탄소 배출 관리 시스템 사용 가이드
                </h6>

                <div class="row">
                    <div class="col-md-6">
                        <h6 class="font-weight-bold">1. 개요</h6>
                        <p class="small text-muted">전체적인 탄소 배출 현황을 한눈에 파악할 수 있습니다.</p>

                        <h6 class="font-weight-bold">2. 배출계수 관리</h6>
                        <p class="small text-muted">
                            탄소 배출 계산에 사용되는 배출계수를 관리합니다. 지역별, 연료별 배출계수를 등록할 수
                            있습니다.
                        </p>

                        <h6 class="font-weight-bold">3. 세팅</h6>
                        <p class="small text-muted">
                            경계 설정 → 활동자료 → 산정 실행 → 목표 설정 순서로 단계별 설정을 진행합니다.
                        </p>

                        <h6 class="font-weight-bold">4. 보고서</h6>
                        <p class="small text-muted">산정된 배출량 데이터를 기반으로 보고서를 생성합니다.</p>
                    </div>
                    <div class="col-md-6">
                        <h6 class="font-weight-bold">세팅 단계 안내</h6>
                        <p class="small text-muted">
                            <strong>Step 1:</strong> 경계 설정 - 조직 및 운영 경계 정의<br />
                            <strong>Step 2:</strong> 활동자료 - 배출 활동 데이터 업로드<br />
                            <strong>Step 3:</strong> 산정 실행 - 배출량 계산 수행<br />
                            <strong>Step 4:</strong> 목표 설정 - 감축 목표 및 로드맵 수립
                        </p>
                    </div>
                </div>

                <div class="alert alert-info mt-3">
                    <i class="mdi mdi-lightbulb mr-2"></i>
                    <strong>팁:</strong> 세팅 버튼을 클릭하여 단계별로 설정을 진행하세요.
                </div>
            </div>

            <template #modal-footer>
                <b-button variant="secondary" @click="showHelpModal = false"> 닫기 </b-button>
            </template>
        </b-modal>

        <!-- 알림 토스트 -->
        <b-toast v-model="showToast" :title="toastTitle" :variant="toastVariant" solid toaster="b-toaster-bottom-right">
            {{ toastMessage }}
        </b-toast>
    </div>
</template>

<script>
import carbonApi from "./api";
import CarbonCodes from "./codes";
import CarbonOverview from "./components/dashboard/DashboardMain.vue";
import ReportModal from "./components/report/ReportModal.vue";
import CarbonHeader from "./components/common/CarbonHeader.vue";
import SettingsModal from "./components/settings/SettingsModal.vue";
import { mapState } from "vuex";

export default {
    name: "CarbonManagement",
    components: {
        CarbonOverview,
        ReportModal,
        CarbonHeader,
        SettingsModal,
    },
    data() {
        // 랜덤 값 생성 함수
        const randomInRange = (min, max) => Math.random() * (max - min) + min;
        const randomArray = (length, min, max) => Array.from({ length }, () => randomInRange(min, max));

        // Scope별 월별 데이터 생성 (12개월)
        const s1Data = randomArray(12, 2500, 3500);
        const s2Data = randomArray(12, 6000, 7000);
        const s3Data = randomArray(12, 2000, 2500);

        // 현재 월(마지막 달) 기준 총합 계산
        const currentS1 = s1Data[11];
        const currentS2 = s2Data[11];
        const currentS3 = s3Data[11];
        const currentTotal = currentS1 + currentS2 + currentS3;

        // 목표값 (현재 총합의 80~90%)
        const targetValue = currentTotal * randomInRange(0.8, 0.9);

        // 진행률 계산 (목표 대비) - 목업 데이터는 40%로 고정
        const progressValue = 40;

        // 전년 대비 감축량 (현재 총합의 5~15%)
        const reductionValue = currentTotal * randomInRange(0.05, 0.15);

        // 카테고리별 데이터 생성
        const categories = [
            { name: "고정연소", value: randomInRange(2000, 4000) },
            { name: "이동연소", value: randomInRange(1500, 2500) },
            { name: "전력", value: randomInRange(4000, 6000) },
            { name: "스팀", value: randomInRange(1000, 2000) },
            { name: "폐기물", value: randomInRange(500, 1000) },
        ];

        const currentYear = new Date().getFullYear();
        const currentMonth = String(currentYear) + String(new Date().getMonth() + 1).padStart(2, "0");
        return {
            orgList: [],
            selectedOrgIdx: null,
            selectedYear: String(currentYear),
            selectedMonth: currentMonth,
            viewMode: "YEAR",
            displayUnit: "tCO2e",
            dashboardLoading: false,
            showSettingsModal: false,
            settingsInitialMenu: null,
            showHelpModal: false,
            showToast: false,
            toastTitle: "",
            toastMessage: "",
            toastVariant: "info",
            // 대시보드 가짜 임시 데이터 (랜덤 생성)
            mockDashboardData: {
                // KPI 데이터
                sampleData: {
                    total: currentTotal,
                    scope: {
                        s1: currentS1,
                        s2: currentS2,
                        s3: currentS3,
                    },
                    target: targetValue,
                    progress: progressValue,
                    reduction: reductionValue,
                    intensity: randomInRange(0.7, 1.0),
                },
                // 월별 추이 데이터 (12개월)
                chartData: {
                    months: ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"],
                    s1: s1Data,
                    s2: s2Data,
                    s3: s3Data,
                    target: Array(12).fill(targetValue),
                },
                // 카테고리별 배출 데이터
                categoryBreakdown: categories,
            },
            boundaryContext: null,
            activitySummary: null,
        };
    },
    computed: {
        ...mapState(["loginUser"]),
    },
    watch: {
        viewMode(newMode) {
            if (newMode === "MONTH") {
                const ym = this.selectedMonth || "";
                if (!ym || ym.length < 6) {
                    const y = parseInt(this.selectedYear, 10) || new Date().getFullYear();
                    const m = new Date().getMonth() + 1;
                    this.selectedMonth = String(y) + String(m).padStart(2, "0");
                } else {
                    this.selectedYear = ym.slice(0, 4);
                }
            }
        },
    },
    async mounted() {
        // 서브모듈 진입 시에도 공통코드 로딩을 kick-off (중복 API 호출은 CarbonCodes.load() 내부에서 방지)
        CarbonCodes.load?.();
        this.fetchDashboardOrgs();
    },
    methods: {
        async fetchDashboardOrgs() {
            try {
                const res = await carbonApi.organization.getOrgs();
                const data = res.data?.data || res.data;
                const list = data?.organizations || data?.list || [];
                this.orgList = Array.isArray(list) ? list : [];
                if (this.orgList.length > 0 && this.selectedOrgIdx == null) {
                    this.selectedOrgIdx = this.orgList[0].orgIdx;
                }
            } catch (e) {
                console.error("[CarbonManagement] 조직 목록 조회 실패:", e);
            }
        },

        handleDashboardRefresh() {
            if (this.$refs.dashboard && typeof this.$refs.dashboard.loadDashboard === "function") {
                this.$refs.dashboard.loadDashboard(true);
            }
        },

        handleDataUpdate() {
            this.showNotification("데이터가 업데이트되었습니다.", "success");
        },

        handleBoundarySaved(boundary) {
            this.boundaryContext = this.normalizeBoundaryContext(boundary);
            const orgName = this.boundaryContext?.selectedOrg?.orgName || "조직";
            this.showNotification(`${orgName} 경계 구성이 저장되었습니다.`, "success");
        },

        handleTargetSaved() {
            this.showNotification("목표가 저장되었습니다.", "success");
        },

        handleDataImported(summary) {
            this.activitySummary = this.normalizeActivitySummary(summary);
            const totalRows = this.activitySummary?.totalRows || 0;
            const successCount = this.activitySummary?.successCount || 0;
            const msg =
                totalRows > 0
                    ? `활동자료 ${totalRows}건이 업로드되었습니다. (성공: ${successCount}건)`
                    : "활동자료가 업로드되었습니다.";
            this.showNotification(msg, "success");
        },

        openReportModal() {
            this.$refs.reportModal?.show();
        },

        handleReportGenerated() {
            this.showNotification("보고서가 생성되었습니다.", "success");
        },

        handleSettingsClick() {
            this.settingsInitialMenu = null;
            this.showSettingsModal = true;
        },

        handleSettingsClose() {
            this.showSettingsModal = false;
            this.settingsInitialMenu = null;
        },

        showNotification(message, variant = "info", title = "알림") {
            this.toastMessage = message;
            this.toastVariant = variant;
            this.toastTitle = title;
            this.showToast = true;
        },

        normalizeActivitySummary(summary) {
            if (!summary || typeof summary !== "object") {
                return {
                    summaryId: Date.now(),
                    totalRows: 0,
                    successCount: 0,
                    errorCount: 0,
                    errors: [],
                };
            }

            return {
                ...summary,
                summaryId: Date.now(),
            };
        },

        normalizeBoundaryContext(boundary) {
            const organizations = Array.isArray(boundary)
                ? boundary.filter(Boolean)
                : Object.values(boundary || {}).filter(Boolean);

            const selectedOrg =
                organizations.find((org) => (org.status || org.orgStatus) === "ACTIVE") || organizations[0] || null;

            const orgBoundaries = selectedOrg?.orgBoundaries || [];
            const selectedOrgBoundary =
                orgBoundaries.find((b) => (b.draftStat || b.status) === "ACTIVE") || orgBoundaries[0] || null;

            const operBoundaries = selectedOrg?.operBoundaries || [];
            const selectedOperBoundary =
                operBoundaries.find((b) => b.isActive === 1 || (b.status || b.draftStat) === "ACTIVE") ||
                operBoundaries[0] ||
                null;

            return {
                contextId: Date.now(),
                raw: boundary,
                organizations,
                selectedOrg,
                selectedOrgBoundary,
                selectedOperBoundary,
            };
        },
    },
};
</script>

<!-- Carbon 플러그인 공통 폼 스타일 (btn-compact, input-compact, select-compact 등) -->
<style lang="scss" src="./styles/_carbon-forms.scss"></style>
<!-- Carbon 플러그인 공통 테이블 스타일 (table-compact, tag-small 등) -->
<style lang="scss" src="./styles/_carbon-tables.scss"></style>
<!-- Bootstrap 4 Carbon 테마 오버라이드 (btn-primary, form-control 포커스 등) -->
<style lang="scss" src="./styles/bootstrap-carbon-overrides.scss"></style>

<style scoped>
/* CSS 변수 정의 - Scope별 색상 체계 */
:root {
    --scope1-color: #ff6b6b;
    --scope1-light: #ffa5a5;
    --scope1-dark: #e05555;
    --scope1-bg: rgba(255, 107, 107, 0.1);

    --scope2-color: #4ecdc4;
    --scope2-light: #7eddd6;
    --scope2-dark: #3ab7ae;
    --scope2-bg: rgba(78, 205, 196, 0.1);

    --scope3-color: #95e1d3;
    --scope3-light: #b8ede2;
    --scope3-dark: #78cfc0;
    --scope3-bg: rgba(149, 225, 211, 0.1);

    --primary-green: #52b788;
    --primary-green-dark: #2d8659;
    --primary-green-light: #74d4a0;

    --engineer-bg: #1e293b;
    --engineer-bg-light: #334155;
    --engineer-border: #475569;
    --engineer-text: #cbd5e1;
}

/* 기본 스타일 리셋 */
label {
    margin-bottom: 0px !important;
}

/* 메인 컨테이너 */
.carbon-management {
    color: #1e293b;
    padding: 0;
    height: 91vh;
}

/* 컨텐츠 컨테이너 */
.content-container {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* 메인 콘텐츠 */
.main-content {
    flex: 1;
    overflow: hidden;
}

.tab-content {
    height: 100%;
    overflow-y: auto;
}

/* 도움말 모달 */
.help-content h6 {
    color: #12462d;
    margin-top: 20px;
    font-weight: 700;
}

.help-content h6:first-child {
    margin-top: 0;
}

.help-content .text-primary {
    color: #0f4a35 !important;
}

.help-content .alert-info {
    background-color: #e8f5e9;
    border-color: #d8f3dc;
    color: #12462d;
}

/* 반응형 디자인 */
@media (max-width: 768px) {
    .carbon-management {
        padding: 0;
    }

    .content-container {
        margin: 0 16px;
        height: calc(100vh - 32px);
    }
}

/* 애니메이션 */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 로딩 상태 */
.mdi-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
